import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { User } from '@shared/types';
import { useUpdateUser, useRoles } from '../../hooks/useUsers';

interface UserEditModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  isApproved: boolean;
  isLockedOut: boolean;
  canMessage: boolean;
  twoFactorEnabled: boolean;
  roles: string[];
}

export const UserEditModal: React.FC<UserEditModalProps> = ({ user, isOpen, onClose }) => {
  const { mutate: updateUser, isPending } = useUpdateUser();
  const { data: availableRoles = [] } = useRoles();

  const { register, handleSubmit, setValue, watch } = useForm<UserFormData>({
    defaultValues: {
      firstName: user.FirstName || '',
      lastName: user.LastName || '',
      email: user.Email,
      isApproved: user.IsApproved,
      isLockedOut: user.IsLockedOut,
      canMessage: user.CanMessage !== false,
      twoFactorEnabled: user.TwoFactorEnabled === true,
      roles: user.roles,
    },
  });

  const selectedRoles = watch('roles');

  useEffect(() => {
    setValue('firstName', user.FirstName || '');
    setValue('lastName', user.LastName || '');
    setValue('email', user.Email);
    setValue('isApproved', user.IsApproved);
    setValue('isLockedOut', user.IsLockedOut);
    setValue('canMessage', user.CanMessage !== false);
    setValue('twoFactorEnabled', user.TwoFactorEnabled === true);
    setValue('roles', user.roles);
  }, [user, setValue]);

  const onSubmit = (data: UserFormData) => {
    updateUser(
      { id: user.UserId, data },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const toggleRole = (roleName: string) => {
    const currentRoles = selectedRoles || [];
    if (currentRoles.includes(roleName)) {
      setValue(
        'roles',
        currentRoles.filter((r) => r !== roleName)
      );
    } else {
      setValue('roles', [...currentRoles, roleName]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white dark:bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
                    Edit User: {user.UserName}
                  </h3>

                  <div className="mt-6 space-y-4">
                    {/* First Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        First Name
                      </label>
                      <input
                        type="text"
                        {...register('firstName')}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Last Name
                      </label>
                      <input
                        type="text"
                        {...register('lastName')}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email
                      </label>
                      <input
                        type="email"
                        {...register('email')}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                      />
                    </div>

                    {/* Status Checkboxes */}
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('isApproved')}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                          Approved
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('isLockedOut')}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                          Locked Out
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('canMessage')}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                          Allow Messages
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('twoFactorEnabled')}
                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                          Require Two-Factor Authentication (2FA)
                        </label>
                      </div>
                    </div>

                    {/* Roles */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Roles
                      </label>
                      <div className="space-y-2">
                        {availableRoles.map((role) => (
                          <div key={role} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedRoles?.includes(role)}
                              onChange={() => toggleRole(role)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              {role}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="submit"
                disabled={isPending}
                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:ml-3 sm:w-auto disabled:opacity-50"
              >
                {isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-600 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
